import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, uniq, filter } from 'lodash-es';

import WarningNotification from '../WarningNotification';
import CheckboxGroup from '../../../CheckboxGroup';
import IOSToggleButton from '../../../IOSToggleButton';
import CrossAppPairingsDropdown from './CrossAppPairingsDropdown';

import { baseDataPointsOptions, advancedDataPointsOptions } from '../../configs/report';

const DataPointsSelect = ({
  id,
  active,
  name = 'Data points',
  options,
  checkboxGroupClassName,
  onChange,
}) => {
  const selectedOptionsSnapshotRef = useRef();
  const optionsValues = useMemo(() => filter(options, { disabled: false }).map(option => option.value), [options]);

  const isAllSelected = !!active.length
    && !!optionsValues.length
    && optionsValues.every(value => active.includes(value));

  const handleSelectAll = (checked) => {
    if (checked) {
      onChange(uniq([...active, ...optionsValues]));
      selectedOptionsSnapshotRef.current = optionsValues.filter(value => active.includes(value));
    } else {
      onChange([
        ...active.filter(item => !optionsValues.includes(item)),
        ...(selectedOptionsSnapshotRef.current || []),
      ]);
      selectedOptionsSnapshotRef.current = null;
    }
  };

  const handleSelectionChange = useCallback((value) => {
    selectedOptionsSnapshotRef.current = null;

    onChange(value);
  }, []);

  // Reset selectedOptionsSnapshotRef when options change
  useEffect(() => {
    selectedOptionsSnapshotRef.current = null;
  }, [options]);

  return (
    <>
      <div className="export-settings__header-left">
        <h4 className="h5 export-settings__header-title">{name}</h4>
        <p className="paragraph-md export-settings__header-description">Select the data you would like exported for your selected entities.</p>
      </div>
      <div className="export-settings-section__toggle-button">
        <IOSToggleButton
          inputID={id}
          checked={isAllSelected}
          inputClass="toggle-switch"
          disabled={!optionsValues.length}
          onChange={handleSelectAll}
        />
        <span>Select all datapoints</span>
      </div>
      <CheckboxGroup
        id="data-points-checkboxes"
        active={active}
        options={options}
        className={cx('export-settings-section__options export-form-checkbox-group data-points-options', checkboxGroupClassName)}
        gapCheckbox="8px"
        size="lg"
        onChange={handleSelectionChange}
      />
    </>
  );
};

DataPointsSelect.propTypes = {
  id: T.string.isRequired,
  active: T.array.isRequired,
  options: T.array.isRequired,
  name: T.string,
  checkboxGroupClassName: T.string,
  onChange: T.func.isRequired,
};

const DataPoints = ({
  report = {},
  isAdvancedDataType,
  onChange,
  onCrossAppsPairsChange,
}) => {
  const { data_points, panel_version } = report;

  if (!data_points) return <span />;

  const options = advancedDataPointsOptions(panel_version).reduce((acc, item) => {
    if (item.value.startsWith('cross_app')) {
      acc.crossApp.push(item);
    } else {
      acc.advanced.push(item);
    }
    return acc;
  }, { advanced: [], crossApp: [] });

  const allDisabled = () => options.crossApp?.every(item => item.disabled);
  const handleAdvancedChange = (value) => {
    onChange(uniq(value));
  };
  const handleCrossAppsPairsChange = (value) => {
    onCrossAppsPairsChange(value);
  };

  return isAdvancedDataType ? (
    <>
      <section className="export-settings-section data-points-section">
        <div className="export-settings-section__content">
          <DataPointsSelect
            id="select-all-advanced-data-points"
            active={data_points}
            name="Advanced Datapoints"
            options={options.advanced}
            checkboxGroupClassName="two-columns"
            onChange={handleAdvancedChange}
          />
        </div>
      </section>
      <div className="export-settings__section-divider" />
      <section className="export-settings-section data-points-section">
        <div className="export-settings-section__content">
          <DataPointsSelect
            id="select-all-cross-app-data-points"
            active={data_points}
            name="Cross-app Datapoints"
            options={options.crossApp}
            checkboxGroupClassName="two-columns"
            onChange={handleAdvancedChange}
          />
          {!allDisabled(data_points) && isEmpty(data_points) && <WarningNotification text="At least one data point must be selected" />}
          {allDisabled(data_points) && <WarningNotification text="Cross-app data is not supported for the selected panel region" />}
        </div>
      </section>
      <section className="export-settings-section cross-apps-section">
        <div className="export-settings-section__content">
          <div className="export-settings__header-left">
            <h4 className="h5 export-settings__header-title">Cross-App Pairings</h4>
            <p className="paragraph-md export-settings__header-description">Select the cross-app pairings you would like to export.</p>
          </div>
          <CrossAppPairingsDropdown
            report={report}
            groupAppsBy={panel_version === 'us-blended-v1-r0-0-public' ? 'sector' : null}
            onChange={handleCrossAppsPairsChange}
          />
        </div>
      </section>
    </>
  ) : (
    <section className="export-settings-section data-points-section">
      <div className="export-settings-section__content">
        <DataPointsSelect
          id="select-all-data-points"
          active={data_points}
          options={baseDataPointsOptions}
          checkboxGroupClassName="three-columns"
          onChange={onChange}
        />
        <p className="serivce-info-text"><sup className="text-rose">*</sup>Only available as CSV</p>
        {isEmpty(data_points) && <WarningNotification text="At least one data point must be selected" />}
      </div>
    </section>
  );
};

DataPoints.propTypes = {
  report: T.object.isRequired,
  isAdvancedDataType: T.bool,
  onChange: T.func.isRequired,
  onCrossAppsPairsChange: T.func.isRequired,
};

export default DataPoints;
